/**
 * AffiliationChips - confirm a tagged person's club for THIS match (ADR-005 P6).
 *
 * Club affiliation is temporal and not necessarily a match team (spectators,
 * loanees, since-transferred players), so it's suggested per (match, person)
 * from lineup → club-at-date → membership and confirmed here. Only the confirmed
 * value drives captions. "Suggest clubs" computes pending suggestions for the
 * match; each pending row is Confirm (optionally edited) / Dismiss.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  confirmAffiliation,
  dismissAffiliation,
  fetchAffiliations,
  refreshAffiliations,
} from '../../../../api';
import styles from './MatchHubSuggestionChip.module.css';

function personLabel(p) {
  return p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `#${p.id}`;
}

function AffiliationRow({ matchId, person, aff, onDone }) {
  const [draft, setDraft] = useState(aff.team_name || '');
  const [busy, setBusy] = useState(false);

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDone(person.id);
    } catch (err) {
      console.error('Affiliation action failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.chip}>
      <span className={styles.from}>{personLabel(person)}</span>
      <span className={styles.arrow}>of</span>
      <input
        className={styles.editInput}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={busy}
      />
      <button
        className={styles.confirm}
        disabled={busy || !draft.trim()}
        onClick={() =>
          act(() =>
            confirmAffiliation(matchId, person.id, draft.trim() !== aff.team_name ? draft.trim() : undefined)
          )
        }
      >
        Confirm
      </button>
      <button
        className={styles.dismiss}
        disabled={busy}
        onClick={() => act(() => dismissAffiliation(matchId, person.id))}
      >
        Dismiss
      </button>
    </div>
  );
}

export default function AffiliationChips({ matchId, persons }) {
  const [affMap, setAffMap] = useState({});
  const [resolved, setResolved] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!matchId) return;
    try {
      const rows = await fetchAffiliations(matchId);
      const m = {};
      rows.forEach((r) => {
        m[r.person_id] = r;
      });
      setAffMap(m);
    } catch (err) {
      console.error('Affiliations load failed:', err);
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!matchId) return null;

  const pending = (persons || []).filter(
    (p) => affMap[p.id] && affMap[p.id].state === 'pending' && affMap[p.id].team_name && !resolved.has(p.id)
  );

  const onSuggest = async () => {
    setBusy(true);
    try {
      await refreshAffiliations(matchId);
      setResolved(new Set());
      await load();
    } catch (err) {
      console.error('Suggest clubs failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.affiliations}>
      <button className={styles.suggestBtn} onClick={onSuggest} disabled={busy}>
        {busy ? 'Suggesting…' : 'Suggest clubs'}
      </button>
      {pending.map((p) => (
        <AffiliationRow
          key={p.id}
          matchId={matchId}
          person={p}
          aff={affMap[p.id]}
          onDone={(pid) => setResolved((prev) => new Set(prev).add(pid))}
        />
      ))}
    </div>
  );
}
